import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LuArrowLeft,
  LuArrowRight,
  LuShare2,
  LuHeart,
  LuBath,
  LuUtensils,
  LuBedDouble,
  LuTrees,
  LuSquareParking,
  LuLayoutGrid,
  LuMapPin,
  LuInfo,
  LuChevronRight,
  LuCircleCheck,
  LuCalendarCheck,
  LuPhone,
} from 'react-icons/lu';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import styles from '../assets/spaceShow.module.css';
import spaceShowService from '../services/spaceShowService';
import spacesService from '../services/spacesService';
import api from '../api';
import PhotoGallery from '../components/PhotoGallery';
import GoogleMap from '../components/GoogleMap';

// Static guardianship criteria (matches the Figma design — same for every property)
const GUARDIAN_CRITERIA = [
  '3 months previous bank statements',
  '3 months previous pay slips',
  'A scanned copy of your passport',
  "An employer's reference",
  'Aged 21 and over',
];
const SECURE_ROOM_CRITERIA = ['1 month upfront', 'Key payment'];

// "Newly listed" badge — only when the listing data actually supports it,
// so a related card is never mislabelled (mirrors SpacesAvailable).
const isNewlyListed = (p) => {
  const raw =
    p.created_at || p.createdAt || p.published_at || p.publishedAt || p.listed_at;
  if (!raw) return false;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return false;
  const days = (Date.now() - date.getTime()) / 86400000;
  return days >= 0 && days <= 30;
};

// Extract the video ID from any common YouTube link format.
const getYouTubeVideoId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  const patterns = [
    /(?:youtube\.com|youtube-nocookie\.com)\/embed\/([\w-]{6,})/i,
    /youtu\.be\/([\w-]{6,})/i,
    /(?:youtube\.com|youtube-nocookie\.com)\/shorts\/([\w-]{6,})/i,
    /(?:youtube\.com|youtube-nocookie\.com)\/v\/([\w-]{6,})/i,
    /[?&]v=([\w-]{6,})/i,
  ];

  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match) return match[1];
  }

  return null;
};

const getYouTubeEmbedUrl = (url) => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

const SpaceShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  // Note: Laravel permission system handles button visibility, not role-based hiding

  // State management
  const [space, setSpace] = useState(null);
  const [info, setInfo] = useState(null);
  const [confirmations, setConfirmations] = useState([]);
  const [search, setSearch] = useState({}); // Laravel search filters preservation
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saved, setSaved] = useState(false); // toolbar "Save" toggle (Figma)
  const [related, setRelated] = useState([]); // "Related properties" strip

  // Photo gallery state — public listing only shows photos where photos.public = 1
  // (filtered server-side in spacesController.js).
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);

    // Debug permission system
    console.log('🔐 [SpaceShow] User permissions check:');
    console.log('  - property_viewings.attend:', hasPermission('property_viewings.attend'));
    console.log('  - property_viewings.keep_informed:', hasPermission('property_viewings.keep_informed'));
    console.log('  - User role:', user?.role);

    try {
      console.log('📡 [SpaceShow] Loading live database data for property:', id);

      // Real API calls - Laravel-compatible (live database only)
      const spaceResponse = await spaceShowService.getSpaceDetails(id);

      // Handle Laravel-compatible response structure
      if (spaceResponse.data) {
        setSpace(spaceResponse.data.data); // Property data
        setInfo(spaceResponse.data.info || null); // Laravel: $info
        setConfirmations(spaceResponse.data.confirmations || []); // Laravel: $confirmations array
        setSearch(spaceResponse.data.search || {}); // Laravel: $search filters

        console.log('✅ SpaceShow - Live database data loaded:', spaceResponse.data.data?.name);
      } else {
        // Fallback to old structure for compatibility
        setSpace(spaceResponse.data);

        const infoResponse = await spaceShowService.getViewingInfo(id);
        setInfo(infoResponse.data);

        const confirmationsResponse = await spaceShowService.getConfirmations(id);
        setConfirmations(confirmationsResponse.data);

        console.log('✅ SpaceShow - Live data loaded (legacy structure)');
      }
    } catch (error) {
      console.error('❌ Error loading space data from database:', error);
      toast.error('Failed to load space details from database. Please check if the property exists.');

      // Set empty data instead of using mock data
      setSpace(null);
      setInfo(null);
      setConfirmations([]);
      setSearch({});
    } finally {
      setLoading(false);
    }
  }, [id, hasPermission, user?.role]);

  const checkNotificationStatus = useCallback(async () => {
    try {
      // Check if user already has a notification for this property
      const notifications = await spaceShowService.getConfirmations(id);
      // Notification status is handled in the conditional button logic (Laravel-style)
      console.log('📝 Notifications fetched for debugging:', notifications?.data?.length || 0);
    } catch (error) {
      console.error('Error checking notification status:', error);
      // Don't show error toast for this, as it's not critical
    }
  }, [id]);

  // Load data
  useEffect(() => {
    loadData();
    checkNotificationStatus();
  }, [loadData, checkNotificationStatus]);

  // Load a few other available properties for the "Related properties" strip.
  // Biased to the same town, falling back to any available space so the strip
  // is not empty. Runs once the current property is loaded.
  useEffect(() => {
    if (!space) return;
    let cancelled = false;

    const exclude = (list) =>
      (list || []).filter((p) => String(p.id) !== String(id));

    const loadRelated = async () => {
      const town = space.town || space.location?.name || '';
      try {
        let list = [];
        if (town) {
          const res = await spacesService.getAvailableSpaces({
            page: 1,
            perPage: 8,
            location: town,
          });
          list = exclude(res.data);
        }
        if (list.length === 0) {
          const res = await spacesService.getAvailableSpaces({ page: 1, perPage: 8 });
          list = exclude(res.data);
        }
        if (!cancelled) setRelated(list.slice(0, 4));
      } catch (err) {
        console.error('Error loading related properties:', err);
        if (!cancelled) setRelated([]);
      }
    };

    loadRelated();
    return () => {
      cancelled = true;
    };
  }, [space, id]);

  // Calculate savings percentage
  // Returns null (not 0) when there is no real vacant-room price, so the UI can
  // show "--" instead of a misleading fake percentage.
  const calculateSavingsPercentage = () => {
    if (!space) return null;
    const marketRent = parseFloat(space.actual_market_rent) || 0;
    const priceFrom = parseFloat(space.price_from) || 0;

    if (marketRent > 0 && priceFrom > 0) {
      return Math.round(((marketRent - priceFrom) / marketRent) * 100);
    }
    return null;
  };

  // Handle request viewing - Laravel equivalent (uses api base URL so deployment hits backend)
  const handleRequestViewing = async () => {
    console.log('🔍 Request viewing clicked for property:', id);

    try {
      // Laravel equivalent: POST /api/property_viewings/property/{id}/request-viewing
      const { data } = await api.post(`propertyviewings/property/${id}/request-viewing`, { propertyId: id });

      if (data.success && data.data?.viewings) {
        console.log('✅ [SpaceShow] Loaded upcoming viewings:', data.data.viewings.length);

        // Update space state with upcoming viewings data from API
        setSpace((prevSpace) => ({
          ...prevSpace,
          viewingsUpcoming: data.data.viewings,
        }));

        // Update confirmations from API
        setConfirmations(data.data.confirmations || []);

        setShowModal(true);
      } else {
        console.error('❌ Failed to load upcoming viewings:', data.message);
        toast.error('Failed to load viewing times. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error loading upcoming viewings:', error);
      toast.error('Failed to connect to server. Please try again.');
    }
  };

  // Handle keep informed - Laravel equivalent (uses api base URL so deployment hits backend)
  const handleKeepInformed = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Keep me informed button clicked for property:', id);

    try {
      // Laravel equivalent: GET /keep-informed/{propertyId}
      const { data } = await api.get(`spaces/keep-informed/${id}`);

      if (data.success) {
        console.log('✅ [SpaceShow] Keep informed successful:', data.message);
        toast.success(data.message); // Laravel flash equivalent
        // Laravel equivalent: redirect to spaces.keep_me_inform
        navigate('/keep-me-informed');
      } else {
        console.error('❌ Keep informed failed:', data.message);
        toast.error(data.message);
      }
    } catch (error) {
      console.error('❌ Error in keep informed:', error);
      toast.error('Failed to set up property notifications. Please try again.');
    }
  };

  // Handle view notifications
  const handleViewNotifications = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('View notifications button clicked - navigating to /keep-me-informed');
    navigate('/keep-me-informed');
  };

  // Handle attend viewing - Laravel equivalent (uses api base URL so deployment hits backend)
  const handleAttendViewing = async (viewingId) => {
    console.log('🔍 I will attend clicked for viewing:', viewingId);

    try {
      // Laravel equivalent: POST /property_viewings/viewing/{id}/attend
      const { data } = await api.post(`propertyviewings/viewing/${viewingId}/attend`, { viewingId: viewingId });

      if (data.success) {
        console.log('✅ [SpaceShow] Successfully confirmed attendance');
        toast.success('You will be notified about this viewing!');

        // Add this viewing to confirmations
        setConfirmations((prev) => [...prev, viewingId]);
      } else {
        console.error('❌ Failed to confirm attendance:', data.message);
        toast.error('Failed to confirm attendance. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error confirming attendance:', error);
      toast.error('Failed to connect to server. Please try again.');
    }
  };

  // Handle cancel viewing - Laravel equivalent: DELETE route('property_viewings.delete.confirmation', $viewing->id)
  const handleCancelViewing = async (viewingId) => {
    console.log('🔍 Cancel clicked for viewing:', viewingId);

    // Laravel equivalent: data-confirm-action="Are you sure you want to cancel?"
    const confirmCancel = window.confirm('Are you sure you want to cancel?');
    if (!confirmCancel) {
      return;
    }

    try {
      // Laravel equivalent: DELETE /property_viewings/viewing/{id}/cancel (uses api base URL for deployment)
      const { data } = await api.delete(`propertyviewings/viewing/${viewingId}/cancel`);

      if (data.success) {
        console.log('✅ [SpaceShow] Successfully cancelled attendance');
        toast.success('Your viewing attendance has been cancelled.');

        // Remove this viewing from confirmations
        setConfirmations((prev) => prev.filter((cid) => cid !== viewingId));
      } else {
        console.error('❌ Failed to cancel attendance:', data.message);
        toast.error('Failed to cancel attendance. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error cancelling attendance:', error);
      toast.error('Failed to connect to server. Please try again.');
    }
  };

  // Handle back to search with Laravel-style filter preservation
  const handleBackToSearch = () => {
    // Laravel equivalent: preserving search filters when returning to available spaces
    const searchParams = new URLSearchParams();

    // Add search filters to URL if they exist (Laravel: $search array)
    Object.entries(search || {}).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== 'no_min' && value !== 'no_max') {
        searchParams.append(key, value);
      }
    });

    const searchString = searchParams.toString();
    const navigationPath = searchString ? `/spaces/available?${searchString}` : '/spaces/available';

    console.log('🔙 SpaceShow - Navigating back with search filters:', searchString);
    navigate(navigationPath);
  };

  // Toolbar: share / save (present in the Figma design)
  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: space?.name || 'Property', url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      }
    } catch (err) {
      // User dismissed the native share sheet — not an error worth surfacing.
      console.log('Share dismissed:', err?.message);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading space details...</div>
      </div>
    );
  }

  if (!space) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Space not found</div>
      </div>
    );
  }

  // Build the public gallery: main photo first, then every photo where
  // photos.public = 1 (backend already filters). Skip the default placeholder
  // since it isn't a real photo.
  const hasRealMainPhoto =
    space.main_photo && space.main_photo !== '/images/default-property.jpg';
  const gallerySlides = [
    ...(hasRealMainPhoto
      ? [{ src: space.main_photo, title: space.name || 'Property' }]
      : []),
    ...((space.photosPublic || []).map((photo, i) => ({
      src: photo.name,
      title: `${space.name || 'Property'} — photo ${i + 1}`,
    }))),
  ];

  const openGalleryAt = (index) => {
    if (gallerySlides.length === 0) return;
    setGalleryIndex(Math.min(Math.max(index, 0), gallerySlides.length - 1));
    setGalleryOpen(true);
  };

  // ---- Gallery layout (main + 3 thumbnails, Figma) ----
  const publicPhotos = space.photosPublic || [];
  const mainSrc = space.main_photo || '/images/default-property.jpg';
  const photoOffset = hasRealMainPhoto ? 1 : 0; // slide index of publicPhotos[0]
  const sideTop = publicPhotos[0];
  const sideBottom = [publicPhotos[1], publicPhotos[2]];
  const extraPhotos = Math.max(0, publicPhotos.length - 3);

  const onImgError = (e) => {
    e.target.onerror = null;
    e.target.src = '/images/default-property.jpg';
  };

  // ---- Pricing card CTA (preserves the original conditional viewing logic) ----
  const hasUpcoming = (space.viewingsUpcoming?.length || 0) > 0;
  const canAttend = hasPermission('property_viewings.attend');
  const canInform = hasPermission('property_viewings.keep_informed');

  let primaryLabel = 'Book a viewing';
  let primaryOnClick;
  let primaryDisabled = false;
  if (hasUpcoming && canAttend) {
    primaryLabel = 'Book a viewing';
    primaryOnClick = handleRequestViewing;
  } else if (!hasUpcoming && canInform && info) {
    primaryLabel = 'View Notifications';
    primaryOnClick = handleViewNotifications;
  } else if (!hasUpcoming && canInform && !info) {
    primaryLabel = 'Keep me Informed';
    primaryOnClick = handleKeepInformed;
  } else {
    // No applicable permission/action (original rendered nothing here).
    primaryDisabled = true;
  }

  // Call agent — use a phone number from the data when present.
  const agentPhone =
    space.agent_phone ||
    space.agentPhone ||
    space.contact_number ||
    space.company?.phone ||
    space.landlord?.phone ||
    null;
  const handleCallAgent = (e) => {
    if (!agentPhone) {
      e.preventDefault();
      toast.info("Agent contact details aren't available for this property.");
    }
  };

  // YouTube preview from the property "video" field (only rendered when valid).
  const videoId = getYouTubeVideoId(space.video);
  const videoEmbedUrl = videoId ? getYouTubeEmbedUrl(space.video) : null;
  const videoThumbUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;

  // ---- Additional information rows (preserves all existing property fields) ----
  const includeItems = [
    space.bedrooms ? { icon: <LuBedDouble />, label: `${space.bedrooms} Bedroom/s` } : null,
    { icon: <LuBath />, label: `${space.bathrooms ?? 0} Bathroom/s` },
    { icon: <LuUtensils />, label: `${space.kitchens ?? 0} Kitchen/s` },
    space.vacantRooms?.length != null
      ? { icon: <LuLayoutGrid />, label: `${space.vacantRooms.length} Available space/s` }
      : null,
    space.spaces ? { icon: <LuLayoutGrid />, label: `${space.spaces} Total space/s` } : null,
    space.location?.name ? { icon: <LuMapPin />, label: space.location.name } : null,
    space.garden ? { icon: <LuTrees />, label: 'Garden' } : null,
    space.parking ? { icon: <LuSquareParking />, label: 'Parking' } : null,
  ].filter(Boolean);

  return (
    <div className={styles.page}>
      {/* Toolbar: Back to results / Share / Save */}
      <div className={styles.toolbar}>
        <button className={styles.backLink} onClick={handleBackToSearch}>
          <LuArrowLeft />
          Back to results
        </button>
        <div className={styles.toolbarActions}>
          <button className={styles.toolbarBtn} onClick={handleShare}>
            <LuShare2 />
            Share
          </button>
          <button
            className={`${styles.toolbarBtn} ${saved ? styles.active : ''}`}
            onClick={() => setSaved((s) => !s)}
            aria-pressed={saved}
          >
            <LuHeart fill={saved ? 'currentColor' : 'none'} />
            Save
          </button>
        </div>
      </div>

      <div className={styles.content}>
        {/* Photo gallery */}
        <div className={`${styles.gallery} ${publicPhotos.length === 0 ? styles.single : ''}`}>
          <div className={styles.galleryMain} onClick={() => openGalleryAt(0)}>
            <img src={mainSrc} alt={space.name || 'Property'} className={styles.galleryImg} onError={onImgError} />
          </div>

          {publicPhotos.length > 0 && (
            <div className={styles.gallerySide}>
              {sideTop && (
                <div className={styles.gallerySideTop} onClick={() => openGalleryAt(photoOffset)}>
                  <img src={sideTop.name} alt="Property view" className={styles.galleryImg} onError={onImgError} />
                </div>
              )}
              {(sideBottom[0] || sideBottom[1]) && (
                <div className={styles.gallerySideBottom}>
                  {sideBottom.map((photo, i) =>
                    photo ? (
                      <div
                        key={i}
                        className={styles.galleryThumb}
                        onClick={() => openGalleryAt(photoOffset + 1 + i)}
                      >
                        <img src={photo.name} alt={`Property view ${i + 2}`} className={styles.galleryImg} onError={onImgError} />
                        {i === 1 && extraPhotos > 0 && (
                          <div className={styles.moreOverlay}>+{extraPhotos}</div>
                        )}
                      </div>
                    ) : null
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Two-column detail layout */}
        <div className={styles.layout}>
          {/* -------------------------------------------------- Main column */}
          <div className={styles.mainCol}>
            <div className={styles.titleBlock}>
              <h1 className={styles.title}>{space.name || space.property_id}</h1>
              {space.public_description ? (
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{ __html: space.public_description }}
                />
              ) : (
                <p className={styles.description}>
                  Global Guardians are offering you this amazing unit in this great location for an
                  excellent price per month.
                </p>
              )}
            </div>

            {/* Additional Information */}
            <div className={styles.section}>
              <h2 className={styles.sectionHeading}>Additional Information</h2>
              <div className={styles.infoCard}>
                <span className={styles.infoCardTitle}>The property includes</span>
                <div className={styles.includeGrid}>
                  {includeItems.map((item, i) => (
                    <div key={i} className={styles.includeRow}>
                      <span className={styles.includeIcon}>{item.icon}</span>
                      {item.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Please Note */}
            <div className={styles.noteCard}>
              <span className={styles.noteTitle}>Please Note:</span>
              <p className={styles.noteBody}>
                To book your initial application for this property, you will need to complete our
                vetting process by uploading of all of your documents. Once approved, we can book
                your visit and to confirm the property you shall make payment of your deposit and
                first month&rsquo;s fee upfront. The notice period you will receive to vacate the
                property will be 28 days, in line with licensee rights.
              </p>
              <p className={styles.noteBody}>
                If you have any questions about our Guardianship scheme, contact us today on
                guardiancare@global-guardians.co.uk. To upload the document and pre-application
                interview, please send us a message with your name, contact number and e-mail
                address.
              </p>
            </div>

            {/* Location map */}
            <div className={styles.mapCard}>
              <GoogleMap properties={[space]} onPropertyClick={() => {}} />
            </div>
          </div>

          {/* -------------------------------------------------- Side column */}
          <div className={styles.sideCol}>
            {/* YouTube video preview — property "video" field */}
            {videoEmbedUrl && (
              <button
                type="button"
                className={styles.videoCard}
                onClick={() => setVideoModalOpen(true)}
                aria-label={`Play ${space.name || 'Property'} video`}
              >
                <span className={styles.videoWrap}>
                  <img
                    className={styles.videoThumb}
                    src={videoThumbUrl}
                    alt={`${space.name || 'Property'} video`}
                  />
                  <span className={styles.videoPlayBadge}>▶</span>
                </span>
              </button>
            )}

            {/* Pricing card */}
            <div className={styles.priceCard}>
              <div className={styles.priceGrid}>
                <div className={`${styles.priceCell} ${styles.priceCellFull}`}>
                  <span className={styles.priceLabel}>Normal area price</span>
                  <span className={styles.priceValue}>£{space.actual_market_rent || 900}</span>
                </div>
                <div className={styles.priceRow2}>
                  <div className={styles.priceCell}>
                    <span className={styles.priceLabel}>You pay</span>
                    <span className={styles.priceValue}>
                      {parseFloat(space.price_from) > 0
                        ? `£${space.price_from}`
                        : '£--'}
                    </span>
                  </div>
                  <div className={styles.priceCell}>
                    <span className={styles.priceLabel}>You save</span>
                    <span className={styles.priceValue}>
                      {calculateSavingsPercentage() != null
                        ? `${calculateSavingsPercentage()}%`
                        : '--'}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className={`${styles.cta} ${styles.ctaPrimary}`}
                onClick={primaryOnClick}
                disabled={primaryDisabled}
              >
                <LuCalendarCheck />
                {primaryLabel}
                <LuArrowRight />
              </button>

              <a
                href={agentPhone ? `tel:${agentPhone}` : '#'}
                className={`${styles.cta} ${styles.ctaSecondary}`}
                onClick={handleCallAgent}
              >
                <LuPhone />
                Call Agent
              </a>
            </div>

            {/* Application criteria info banner */}
            <div className={styles.criteriaBanner}>
              <div className={styles.bannerAccent} />
              <div className={styles.bannerBody}>
                <div className={styles.bannerTitleRow}>
                  <LuInfo />
                  <span className={styles.bannerTitle}>View full application criteria</span>
                </div>
                <p className={styles.bannerText}>
                  Please review this information before you create your account. This ensures that
                  you are the right candidate for guardianship.
                </p>
              </div>
              <span className={styles.bannerChevron}>
                <LuChevronRight />
              </span>
            </div>

            {/* Property Guardian Criteria */}
            <div className={styles.criteriaBlock}>
              <span className={styles.criteriaHeading}>Property Guardian Criteria</span>
              <div className={styles.criteriaList}>
                {GUARDIAN_CRITERIA.map((text) => (
                  <div key={text} className={styles.criteriaItem}>
                    <span className={styles.criteriaCheck}>
                      <LuCircleCheck />
                    </span>
                    <span className={styles.criteriaText}>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Secure a room */}
            <div className={styles.criteriaBlock}>
              <span className={styles.criteriaHeading}>We require the following to secure a room:</span>
              <div className={styles.criteriaList}>
                {SECURE_ROOM_CRITERIA.map((text) => (
                  <div key={text} className={styles.criteriaItem}>
                    <span className={styles.criteriaCheck}>
                      <LuCircleCheck />
                    </span>
                    <span className={styles.criteriaText}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Related properties */}
        {related.length > 0 && (
          <section className={styles.relatedSection}>
            <h2 className={styles.relatedHeading}>Related properties</h2>
            <div className={styles.relatedGrid}>
              {related.map((p) => {
                const rPrice = p.price_from
                  ? `£${p.price_from}`
                  : '£--';
                const rSpaces = p.vacantRooms?.length || 0;
                return (
                  <article
                    key={p.id}
                    className={styles.relatedCard}
                    onClick={() => navigate(`/spaces/show/${p.id}`)}
                  >
                    <div className={styles.relatedImageWrap}>
                      <img
                        src={p.main_photo || '/images/default-property.jpg'}
                        alt={p.name || 'Property'}
                        className={styles.relatedImage}
                        loading="lazy"
                        onError={onImgError}
                      />
                      {isNewlyListed(p) && (
                        <span className={styles.relatedBadge}>Newly listed</span>
                      )}
                    </div>
                    <div className={styles.relatedBody}>
                      <div className={styles.relatedPriceRow}>
                        <span className={styles.relatedPrice}>{rPrice}</span>
                        <span className={styles.relatedPriceUnit}>per month</span>
                      </div>
                      <p className={styles.relatedAddress}>
                        {p.address_1}
                        {p.town ? `, ${p.town}` : ''}
                        <br />
                        {p.postcode}
                      </p>
                      <div className={styles.relatedChips}>
                        <span className={styles.relatedChip}>
                          <LuBath />
                          {p.bathrooms ?? 0}
                        </span>
                        <span className={styles.relatedChip}>
                          <LuUtensils />
                          {p.kitchens ?? 0}
                        </span>
                        <span className={styles.relatedChip}>
                          <LuLayoutGrid />
                          {rSpaces} {rSpaces === 1 ? 'space' : 'spaces'}
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* Request Viewing Modal */}
      {showModal && (
        <div
          id="available-space-request-viewing"
          className={`${styles.modal} ${styles.fade}`}
          role="dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
            }
          }}
        >
          <div className={styles.modalDialog}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h4 className={styles.modalTitle}>Request viewing</h4>
                <button
                  type="button"
                  className={`${styles.close} ${styles.btnModalClose}`}
                  onClick={() => setShowModal(false)}
                >
                  &times;
                </button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.tableResponsive}>
                  <table className={`${styles.table} ${styles.tableStriped} ${styles.tableHover} ${styles.tableCondensed}`}>
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>From</th>
                        <th>To</th>
                        <th className={`${styles.actionHead} ${styles.hiddenPrint}`}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {space?.viewingsUpcoming?.map((viewing) => (
                        <tr key={viewing.id}>
                          <td>{viewing.day}</td>
                          <td>{viewing.time_from}</td>
                          <td>{viewing.time_to}</td>
                          <td className={styles.hiddenPrint}>
                            {!confirmations.includes(viewing.id) ? (
                              <button
                                className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary} ${styles.btnAction}`}
                                onClick={() => handleAttendViewing(viewing.id)}
                              >
                                I will attend
                              </button>
                            ) : (
                              <button
                                className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger} ${styles.btnAction}`}
                                onClick={() => handleCancelViewing(viewing.id)}
                              >
                                Cancel
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnDefault} ${styles.btnModalClose}`}
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* YouTube video modal */}
      {videoModalOpen && (
        <div
          className={`${styles.modal} ${styles.fade}`}
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setVideoModalOpen(false);
          }}
        >
          <div className={`${styles.modalDialog} ${styles.videoModalDialog}`}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h4 className={styles.modalTitle}>{space?.name || 'Property'} video</h4>
                <button
                  type="button"
                  className={`${styles.close} ${styles.btnModalClose}`}
                  onClick={() => setVideoModalOpen(false)}
                >
                  &times;
                </button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.videoModalWrap}>
                  <iframe
                    className={styles.videoFrame}
                    src={`${videoEmbedUrl}?autoplay=1`}
                    title={`${space?.name || 'Property'} video`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Large image viewer — zoom / keyboard / fullscreen / prev-next / counter */}
      <PhotoGallery
        open={galleryOpen}
        close={() => setGalleryOpen(false)}
        slides={gallerySlides}
        index={galleryIndex}
      />
    </div>
  );
};

export default SpaceShow;
